import React from "react";
import { BarChart3, Plus, X, ListChecks, Type, ArrowUpDown, Percent } from "lucide-react";

export const POLL_TYPES = [
  { key: "mc", label: "Multiple choice", icon: ListChecks },
  { key: "short", label: "Short answer", icon: Type },
  { key: "ranking", label: "Ranking", icon: ArrowUpDown },
  { key: "sum", label: "Point split (100)", icon: Percent },
];

const blank = () => ({ type: "mc", text: "", options: ["", ""] });

// Controlled multi-question poll builder (up to 10 questions).
export function PollComposer({ questions, onChange }) {
  const setQ = (i, patch) => onChange(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const addQ = () => questions.length < 10 && onChange([...questions, blank()]);
  const removeQ = (i) => onChange(questions.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3" data-testid="poll-composer">
      {questions.map((q, i) => {
        const needsOptions = q.type !== "short";
        return (
          <div key={i} className="rounded-xl p-3" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)" }} data-testid={`poll-q-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Question {i + 1}</span>
              {questions.length > 1 && (
                <button type="button" onClick={() => removeQ(i)} data-testid={`poll-remove-q-${i}`} style={{ color: "var(--destructive)" }}><X size={15} /></button>
              )}
            </div>
            <input value={q.text} onChange={(e) => setQ(i, { text: e.target.value })} placeholder="Ask a question…"
              data-testid={`poll-q-text-${i}`}
              className="w-full rounded-lg px-3 py-2 text-sm outline-none mb-2" style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
            <div className="grid grid-cols-4 gap-1 mb-2">
              {POLL_TYPES.map(({ key, label, icon: Icon }) => (
                <button key={key} type="button" onClick={() => setQ(i, { type: key })} data-testid={`poll-type-${i}-${key}`}
                  title={label}
                  className="rounded-lg py-1.5 flex flex-col items-center gap-0.5"
                  style={q.type === key ? { background: "var(--accent)", color: "#fff" } : { background: "var(--overlay)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}>
                  <Icon size={13} /><span className="text-[8px] leading-tight text-center">{label.split(" ")[0]}</span>
                </button>
              ))}
            </div>
            {needsOptions && (
              <div className="space-y-1.5">
                {(q.options || []).map((o, oi) => (
                  <div key={oi} className="flex items-center gap-1.5">
                    <input value={o} onChange={(e) => { const opts = [...q.options]; opts[oi] = e.target.value; setQ(i, { options: opts }); }}
                      placeholder={`Option ${oi + 1}`} data-testid={`poll-q-${i}-opt-${oi}`}
                      className="flex-1 rounded-lg px-3 py-1.5 text-sm outline-none" style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
                    {q.options.length > 2 && <button type="button" onClick={() => setQ(i, { options: q.options.filter((_, x) => x !== oi) })} style={{ color: "var(--text-muted)" }}><X size={13} /></button>}
                  </div>
                ))}
                {q.options.length < 6 && (
                  <button type="button" onClick={() => setQ(i, { options: [...q.options, ""] })} data-testid={`poll-q-${i}-add-opt`} className="text-xs gold-text">+ Add option</button>
                )}
                {q.type === "sum" && <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>Voters split 100 points across these options.</div>}
              </div>
            )}
          </div>
        );
      })}
      {questions.length < 10 && (
        <button type="button" onClick={addQ} data-testid="poll-add-question"
          className="w-full rounded-xl py-2.5 text-sm flex items-center justify-center gap-1.5" style={{ background: "var(--overlay)", border: "1px dashed var(--border)", color: "var(--text-secondary)" }}>
          <Plus size={15} /> Add question ({questions.length}/10)
        </button>
      )}
    </div>
  );
}

// Cleans composer questions into API payload; returns null if nothing valid.
export function pollPayload(questions) {
  const qs = (questions || [])
    .map((q) => ({ type: q.type, text: (q.text || "").trim(), options: (q.options || []).map((o) => o.trim()).filter(Boolean) }))
    .filter((q) => q.text && (q.type === "short" || q.options.length >= 2));
  return qs.length ? { questions: qs } : null;
}

export { BarChart3 };
